import viewService from "@/services/viewService";
import EntityService from "@/services/entityService";

const finRecService = new EntityService("FinRec");
const clienteService = new EntityService("Cliente");
const projectService = new EntityService("Project");

const isPossivelId = (valor) => {
  if (!valor) return false;
  const texto = String(valor).trim();
  return /^[0-9a-f]{24}$/i.test(texto) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(texto);
};

const extrairListaGetAll = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const carregarMapaPorIds = async (service, ids = [], camposNome = []) => {
  const idsValidos = [...new Set(ids.filter((id) => isPossivelId(id)).map((id) => String(id)))];
  if (!idsValidos.length) return {};

  try {
    const response = await service.getAll(1, Math.max(100, idsValidos.length), "", "", idsValidos, null, null, null);
    const lista = extrairListaGetAll(response);

    return (Array.isArray(lista) ? lista : []).reduce((acc, item) => {
      const id = String(item?.id || item?._id || "");
      if (!id) return acc;

      let nome = "";
      for (const campo of camposNome) {
        if (item?.[campo]) {
          nome = String(item[campo]);
          break;
        }
      }

      if (nome) acc[id] = nome;
      return acc;
    }, {});
  } catch (error) {
    return {};
  }
};

const toNumber = (valor) => {
  const numero = Number(valor);
  return Number.isFinite(numero) ? numero : 0;
};

const obterDataRecebimento = (linha = {}) => {
  if (Array.isArray(linha.recebimentos) && linha.recebimentos.length) {
    const ultima = linha.recebimentos[linha.recebimentos.length - 1];
    return ultima?.data || ultima?.dataRecebimento || null;
  }
  return linha.dataRecebimento || null;
};

const normalizarLinha = (linha = {}) => {
  const valorBruto = toNumber(linha.valorOriginal || linha.valorBruto || 0);
  const impostos = toNumber(linha.impostos || linha.valorImpostos || 0);

  return {
    cliente: linha.clienteNomeResolvido || linha.clienteNome || linha.cliente || "-",
    doc: linha.codigo || linha.doc || "-",
    valorBruto,
    impostos,
    valorLiquido: toNumber(linha.valorLiquido || valorBruto - impostos),
    vencimento: linha.dataVencimento || linha.vencimento || null,
    job: linha.jobCodeResolvido || linha.jobCode || linha.job || "-",
    dataRecebimento: obterDataRecebimento(linha),
    banco: linha.contaFinanceiraNome || linha.banco || "-",
    saldo: toNumber(linha.valorSaldo || linha.saldo || 0)
  };
};

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const possuiDadosValidos = (response) => {
  if (!response) return false;
  if (response?.success === false) return false;
  if (response?.data?.success === false) return false;
  return Array.isArray(response?.data?.data?.data) || Array.isArray(response?.data);
};

const listarPorEntidade = async () => {
  const response = await finRecService.getAll(1, 5000, "", "", null, null, null, "dataVencimento");
  return extrairLista(response);
};

const listarPorView = async (params = []) => {
  const viewNames = [
    "relatorio-contas-receber",
    "a1d4984f-524b-4246-8d40-2c6b8776109d",
    "finrec"
  ];

  for (const viewName of viewNames) {
    const response = await viewService.viewRun(viewName, 1, params);
    if (possuiDadosValidos(response)) {
      return extrairLista(response);
    }
  }

  return [];
};

const enriquecerComNomes = async (lista = []) => {
  const listaBase = Array.isArray(lista) ? lista : [];

  const clienteIds = listaBase
    .map((item) => item?.cliente || item?.clienteId)
    .filter((valor) => isPossivelId(valor));

  const jobIds = listaBase
    .map((item) => item?.job || item?.project || item?.jobId || item?.projectId)
    .filter((valor) => isPossivelId(valor));

  const [mapaClientes, mapaJobs] = await Promise.all([
    carregarMapaPorIds(clienteService, clienteIds, ["nomeFantasia", "razaoSocial", "name", "nome"]),
    carregarMapaPorIds(projectService, jobIds, ["code", "codigo", "name", "nome"])
  ]);

  return listaBase.map((item) => {
    const clienteId = String(item?.cliente || item?.clienteId || "");
    const jobId = String(item?.job || item?.project || item?.jobId || item?.projectId || "");

    return {
      ...item,
      clienteNomeResolvido:
        item?.clienteNome ||
        item?.cliente_name ||
        item?.nomeCliente ||
        mapaClientes[clienteId] ||
        (isPossivelId(clienteId) ? "-" : clienteId),
      jobCodeResolvido:
        item?.jobCode ||
        item?.projectCode ||
        item?.job_codigo ||
        mapaJobs[jobId] ||
        (isPossivelId(jobId) ? "-" : jobId)
    };
  });
};

export default {
  async listarContasAReceber(filtros = {}) {
    const params = [];

    if (filtros.dataInicio) {
      params.push({ dataType: "String", name: "dataInicio", value: filtros.dataInicio });
    }

    if (filtros.dataFim) {
      params.push({ dataType: "String", name: "dataFim", value: filtros.dataFim });
    }

    if (filtros.cliente) {
      params.push({ dataType: "String", name: "cliente", value: filtros.cliente });
    }

    if (filtros.job) {
      params.push({ dataType: "String", name: "job", value: filtros.job });
    }

    let lista = await listarPorView(params);

    if (!lista.length) {
      lista = await listarPorEntidade();
    }

    lista = await enriquecerComNomes(lista);

    return lista.map((linha) => normalizarLinha(linha));
  }
};
